import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import * as api from "api";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
  NumericCell,
  useDisableNumericWheel,
} from "../../../DibursementPayment/DisbursementInputField";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import SubmitButton from "components/common/SubmitButton";

const UserTimeTargets = (props) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const weeklyWheelRef = useDisableNumericWheel();

  const TimeTargetCell = React.useCallback(
    (cellProps) => <NumericCell {...cellProps} format="n0" min={0} />,
    []
  );

  const sumDayValues = (list) =>
    (list || []).reduce((acc, item) => acc + (Number(item.value) || 0), 0);

  const [dataState, setData] = React.useState(props.data);
  const [weeklyTarget, setWeeklyTarget] = React.useState(
    sumDayValues(props.data)
  );

  const hasDayValues = (dataState || []).some(
    (item) => Number(item.value) > 0
  );

  // Sync weekly field with the sum of day targets.
  React.useEffect(() => {
    setWeeklyTarget(sumDayValues(dataState));
  }, [dataState]);

  const handleWeeklyBlur = () => {
    const allDaysZero = dataState.every(
      (item) => !item.value || Number(item.value) === 0
    );

    if (allDaysZero && Number(weeklyTarget) > 0) {
      // Spread weekly target across days, distributing the remainder.
      const numDays = dataState.length;
      const base = Math.floor(Number(weeklyTarget) / numDays);
      let remainder = Number(weeklyTarget) - base * numDays;
      const newData = dataState.map((item) => {
        let value = base;
        if (remainder > 0) {
          value += 1;
          remainder -= 1;
        }
        return { ...item, value };
      });
      setData(newData);
    } else {
      // Days already set: just reflect the total.
      setWeeklyTarget(sumDayValues(dataState));
    }
  };

  const handleClearDayTargets = () => {
    const newData = dataState.map((item) => ({ ...item, value: 0 }));
    setData(newData);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let data = [];
    dataState.map((item_1) => {
      if (item_1.weekDayNo !== undefined) {
        let dataTemp = {};
        dataTemp.weekDayNo = item_1.weekDayNo;
        dataTemp.value = item_1.value;
        data.push(dataTemp);
      }
    });
    try {
      await api.editTimeTargets(props.item.userID, data);
      if (props.onRefresh) {
        await props.onRefresh();
      }
    } catch (error) {
      return;
    } finally {
      setIsSubmitting(false);
    }

    props.cancelEdit();
  };

  const itemChange = (event) => {
    const newData = dataState.map((item) => {
      if (item.counter === event.dataItem.counter) {
        return { ...item, [event.field]: event.value };
      }
      return item;
    });
    setData(newData);
  };

  return (
    <Dialog
      onClose={props.cancelEdit}
      className="dialog-lg h-100"
      title={`Time Target - ${props.item?.displayname} (${props.item?.userRef})`}
    >
      <div className="d-flex flex-column h-100 w-100">
        <div
          className="flex-grow-1 d-flex flex-column overflow-hidden px-4 py-3 mx-auto w-100"
          style={{ maxWidth: "80%", minHeight: 0 }}
        >
          <p className="fs-md fw-semibold mb-2">Daily Targets</p>
          <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
            <Grid
              data={dataState}
              onItemChange={itemChange}
              style={{
                margin: "auto",
                height: "100%",
                width: "100%",
                overflow: "auto",
              }}
              className="mx-auto"
            >
              <Column field="weekDayName" title="Day" />
              <Column field="value" title="Target (Hours)" cell={TimeTargetCell} />
            </Grid>
          </div>
          <div className="d-flex justify-content-end mb-3 mt-3">
            <button
              type="button"
              className="button-small bgBtnSecondary border-white d-inline-flex align-items-center justify-content-center"
              style={{ paddingTop: 0, paddingBottom: 0 }}
              onClick={handleClearDayTargets}
              disabled={!hasDayValues}
            >
              Clear
            </button>
          </div>

          <div>
            <p className="fs-lg fw-semibold mb-1">Time Target (Hours)</p>
            <div className="w-100 border-bottom border-opacity-50"></div>
          </div>
          <div className="d-flex flex-column align-items-end mt-3">
            <div className="right-align d-flex align-items-center justify-content-end">
              <div style={{ width: "100px" }}>
                <p className="fs-md fw-semibold mb-0">Weekly</p>
              </div>
              <NumericTextBox
                ref={weeklyWheelRef}
                width={150}
                style={{ textAlign: "right" }}
                value={weeklyTarget}
                onChange={(e) => setWeeklyTarget(e.value ?? 0)}
                onBlur={handleWeeklyBlur}
                placeholder="0"
                spinners={false}
                format="n0"
                min={0}
              />
            </div>
            <div className="right-align d-flex align-items-center justify-content-end mt-2">
              <div style={{ width: "100px" }}>
                <p className="fs-md fw-semibold mb-0">Monthly</p>
              </div>
              <NumericTextBox
                width={150}
                style={{ textAlign: "right" }}
                value={(weeklyTarget || 0) * 4.35}
                readOnly={true}
                placeholder="0"
                spinners={false}
                format="n0"
              />
            </div>
            <div className="right-align d-flex align-items-center justify-content-end mt-2">
              <div style={{ width: "100px" }}>
                <p className="fs-md fw-semibold mb-0">Yearly</p>
              </div>
              <NumericTextBox
                width={150}
                style={{ textAlign: "right" }}
                value={(weeklyTarget || 0) * 52}
                readOnly={true}
                placeholder="0"
                spinners={false}
                format="n0"
              />
            </div>
          </div>
        </div>
        <div className="dialog-footer flex-shrink-0">
          <button
            className="common-btn bgBtnSecondary border-white me-3"
            onClick={props.cancelEdit}
            type={"submit"}
          >
            Cancel
          </button>
          <SubmitButton
            className="k-button common-btn me-4"
            type={"submit"}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </SubmitButton>
        </div>
      </div>
    </Dialog>
  );
};
export default UserTimeTargets;
